import { Link } from "@inertiajs/react"
import { MarketingFooter } from "@/components/marketing-footer"
import { Button } from "@/components/ui/button"
import { Linkedin } from "lucide-react"

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

export default function About() {
  // Props are available via usePage if needed, but not used directly here anymore
  // const { canonicalUrl } = useMemo(...)


  return (
    <>


      {/* Hero Section */}
      <div className="border-b bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="mb-6">
            <nav className="text-muted-foreground flex items-center space-x-2 text-sm">
              <Link
                href="/"
                className="transition-colors hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                Home
              </Link>
              <span>/</span>
              <span className="text-foreground font-medium">About</span>
            </nav>
          </div>

          <div className="max-w-3xl">
            <h1 className="text-foreground mb-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              About ThaiCopilot
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Helping expats and travelers learn practical Thai through
              AI-powered conversations and spaced repetition flashcards.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen bg-white dark:bg-slate-950">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="prose prose-slate dark:prose-invert prose-headings:font-bold prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4 prose-p:text-base prose-p:leading-relaxed prose-p:text-slate-700 dark:prose-p:text-slate-300 prose-li:text-base prose-li:leading-relaxed max-w-none">
            {/* My Journey */}
            <section>
              <h2>My Thai Learning Journey</h2>
              <p>
                When I moved to Thailand, I did what most expats do—enrolled in
                an online course. It was just 3 hours long, but it taught me
                essential phrases that got me through the first few weeks.
              </p>
              <p>
                Then I tried Thai classes. Honestly? It was a waste of time for
                the most part. Other students felt the same way. The teacher's
                communication skills matter a lot for classes to work, and
                finding the right fit is hard.
              </p>
              <p>
                The real challenge was <strong>retention</strong>. I'd learn
                something in class, then forget it by the next week. Being a
                software professional, I started building scripts that would
                create flashcards with audio. It worked like a charm — I was
                finally retaining what I learned.
              </p>
              <p>
                But I realized the approach wasn't scalable. I could refine it
                further with AI, and that's exactly what I'm doing now with
                ThaiCopilot.
              </p>
              <p>
                I'm guilty of focusing too much on making the learning process
                more efficient for myself. But in the long run, I believe it'll
                pay dividends — for me and for others on the same journey.
              </p>
            </section>

            {/* The Challenge */}
            <section>
              <h2>Why Thai is Hard</h2>
              <p>
                Learning Thai as an expat is challenging. The language has a
                unique script, five tones that change word meanings entirely,
                and cultural context that's hard to grasp from textbooks.
              </p>
              <ul>
                <li>
                  <strong>Tones:</strong> Same syllable, different tone,
                  completely different meaning. I used to joke that I'm tone
                  deaf. But after talking to many learners, I think tones are
                  overrated for beginners. Context helps a lot—just listen to
                  native speakers and mimic.
                </li>
                <li>
                  <strong>Script:</strong> Thai script is beautiful but
                  intimidating — 44 consonants, 32 vowels, and no spaces between
                  words
                </li>
                <li>
                  <strong>Your Background Matters:</strong> Thai has many words
                  borrowed from Sanskrit and Chinese. If you have exposure to
                  either, you might find some words familiar
                </li>
              </ul>
              <p>
                In the end, learning to speak Thai is a journey. Your journey
                will be different from others because it depends on your
                background, goals, and how you learn best.
              </p>
            </section>

            {/* The Solution */}
            <section>
              <h2>The Solution: ThaiCopilot</h2>
              <p>
                ThaiCopilot is built on the science of adult language learning
                and my experience talking to dozens of language learners about
                what actually works. Adults learn differently than kids—we need
                context, practical application, and efficient methods that fit
                into busy lives.
              </p>
              <p>
                That's why ThaiCopilot focuses on conversational practice and
                spaced repetition, not grammar drills or rote memorization.
              </p>

              <h3>Key Features:</h3>
              <ul>
                <li>
                  <strong>AI Conversations:</strong> Practice real-world
                  scenarios (ordering food, asking for directions, casual small
                  talk) with an AI that understands context and provides
                  feedback
                </li>
                <li>
                  <strong>Smart Flashcards:</strong> Build vocabulary using
                  spaced repetition—the most effective method for long-term
                  retention
                </li>
                <li>
                  <strong>Progress Tracking:</strong> See your learning streaks,
                  mastered words, and areas for improvement
                </li>
                <li>
                  <strong>Practical Focus:</strong> Learn phrases and vocabulary
                  you'll actually use in Thailand, not academic Thai from
                  textbooks
                </li>
              </ul>
            </section>

            {/* Current Stage */}
            <section>
              <h2>Where We Are Now</h2>
              <p>
                ThaiCopilot is in early MVP (Minimum Viable Product) stage. I'm
                personally onboarding the first cohort of users—friends, family,
                and members of Thai-learning communities I'm part of - to gather
                feedback before opening to the public.
              </p>
              <p>This early stage means:</p>
              <ul>
                <li>
                  <strong>Free Access:</strong> Currently free while we gather
                  feedback and improve the platform
                </li>
                <li>
                  <strong>Active Development:</strong> Features are being added
                  and refined based on real user needs
                </li>
                <li>
                  <strong>Direct Feedback Loop:</strong> As a small team (solo
                  founder!), I personally respond to every message and implement
                  suggestions quickly
                </li>
                <li>
                  <strong>Rapid Iteration:</strong> We ship improvements weekly
                  based on what users tell us they need
                </li>
              </ul>
            </section>

            {/* Mission */}
            <section>
              <h2>Our Mission</h2>
              <p>
                My goal is simple: help expats and travelers feel confident
                communicating in Thai. Whether you're ordering street food,
                chatting with your neighbors, or navigating Bangkok's markets, I
                want Thai to feel accessible, not intimidating.
              </p>
              <p>
                Language learning should be practical, engaging, and fit into
                your daily life. That's what I'm building with ThaiCopilot.
              </p>
            </section>

            {/* About the Founder */}
            <section>
              <h2>About the Founder</h2>
              <div className="not-prose space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-8 dark:border-slate-800 dark:bg-slate-900">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900">
                      <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                        R
                      </span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-foreground mb-2 text-xl font-bold">
                      Hi, I'm Rahul
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      Software engineer with 10+ years of experience, now
                      building solo. Moved to Thailand 2 years ago and hit the
                      language barrier hard. Built ThaiCopilot to solve it for
                      myself—been using it for months. It works for me, so it
                      should work for you.
                    </p>
                  </div>
                </div>

                {/* Video Intro */}
                <div className="border-t border-slate-200 pt-4 dark:border-slate-800">
                  <p className="text-muted-foreground mb-3 text-sm font-medium">
                    Quick intro (no voiceover, but translation audio will play):
                  </p>
                  <div className="flex justify-center">
                    <iframe
                      width="315"
                      height="560"
                      src="https://www.youtube.com/embed/knpU5JGA5S4"
                      title="ThaiCopilot Intro"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="rounded-lg"
                    />
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4 dark:border-slate-800">
                  <p className="text-muted-foreground text-sm">
                    <strong className="text-foreground">
                      Why I'm building this:
                    </strong>{" "}
                    I believe learning Thai shouldn't be hard. With the right
                    tools, anyone can build practical conversational skills.
                    Read my full story on{" "}
                    <a
                      href="https://blog.thaicopilot.com/2025/11/why-im-building-thai-copilot.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      the blog
                    </a>
                    .
                  </p>
                </div>

                <div className="flex items-center gap-4 border-t border-slate-200 pt-4 dark:border-slate-800">
                  <a
                    href="https://www.linkedin.com/in/iamrahulroy/"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm transition-colors"
                  >
                    <Linkedin className="h-4 w-4" />
                    <span>LinkedIn</span>
                  </a>
                  <a
                    href="https://x.com/iamrahulroy"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm transition-colors"
                  >
                    <XIcon className="h-4 w-4" />
                    <span>X</span>
                  </a>
                </div>
              </div>
            </section>

            {/* Contact CTA */}
            <section className="not-prose mt-12 text-center">
              <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50 p-12 dark:border-indigo-900 dark:from-indigo-950/30 dark:to-purple-950/30">
                <h2 className="text-foreground mb-4 text-3xl font-bold">
                  Get in Touch
                </h2>
                <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-lg">
                  Have questions, feedback, or want to chat about Thai learning?
                  I'd love to hear from you. I personally read and respond to
                  every message.
                </p>
                <div className="flex flex-col justify-center gap-4 sm:flex-row">
                  <Button asChild size="lg" className="px-8">
                    <Link href="/contact">Contact Us</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="px-8">
                    <a href="mailto:rahul@thaicopilot.com">Email Directly</a>
                  </Button>
                </div>
              </div>
            </section>

            {/* Legal Links */}
            <section className="not-prose mt-12">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="text-foreground mb-4 text-sm font-semibold tracking-wider uppercase">
                  Legal & Privacy
                </h3>
                <ul className="space-y-3 text-sm">
                  <li>
                    <Link
                      href="/privacy-policy"
                      className="text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      Privacy Policy
                    </Link>
                    <span className="text-muted-foreground">
                      {" "}
                      — How we protect your data
                    </span>
                  </li>
                  <li>
                    <Link
                      href="/terms-of-service"
                      className="text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      Terms of Service
                    </Link>
                    <span className="text-muted-foreground">
                      {" "}
                      — Terms and conditions
                    </span>
                  </li>
                  <li>
                    <Link
                      href="/contact"
                      className="text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      Contact
                    </Link>
                    <span className="text-muted-foreground">
                      {" "}
                      — Get in touch
                    </span>
                  </li>
                </ul>
              </div>
            </section>
          </div>
        </div>
      </div>

      <MarketingFooter />
    </>
  )
}
